import React, { memo, useCallback } from "react";
import TruncatedCell from "./TruncatedCell";

/**
 * TableBody Component
 * Renders table rows with merchant data or empty state message
 *
 * @param {Object[]} merchants - Array of merchant data objects
 * @param {string[]} headers - Array of column headers for colspan
 * @param {Function} onBusinessClick - Handler for business name click
 * @param {string} company - Company filter to determine which columns to show
 * @param {boolean} isMerchantListView - Whether this table is used in MerchantListView
 */
const TableBody = memo(({ merchants = [], headers = [], onBusinessClick, company, isMerchantListView = false }) => {
  // Memoize click handler to prevent unnecessary re-renders
  const handleBusinessClick = useCallback((merchant) => {
    // Clear focus from the clicked row to remove green border
    if (document.activeElement) {
      document.activeElement.blur();
    }
    
    onBusinessClick?.(merchant);
  }, [onBusinessClick]);


  // Show empty state if no data
  if (merchants.length === 0) {
    return (
      <tbody className="empty-table-body">
        <tr>
          <td colSpan={headers.length} className="text-center py-5 empty-state-cell">
            <div className="empty-state-content">
              <div className="empty-state-icon">📋</div>
              <div className="empty-state-text">No records found</div>
              <div className="empty-state-subtext">Try adjusting your filters or search criteria</div>
            </div>
          </td>
        </tr>
      </tbody>
    );
  }

  // Determine if this is a simple 2-column table (like Incentive view)
  const isSimpleTable = headers.length === 2;

  // Render merchant data rows with optimized keys
  return (
    <tbody>
      {merchants.map((merchant, index) => {
        // For simple 2-column tables, render only businessName and salesRep
        if (isSimpleTable) {
          return (
            <tr 
              key={`merchant-${merchant.id || merchant.businessName}-${index}`}
              className="simple-row"
            >
              {/* Business Name / Sales Agent */}
              <TruncatedCell 
                className="business-name-cell" 
                style={{ verticalAlign: 'middle' }}
                showTooltip={false}
              >
                <span className="business-name-text">
                  {merchant.businessName}
                </span>
              </TruncatedCell>

              {/* Apps Approved / Count */}
              <TruncatedCell 
                style={{ verticalAlign: 'middle' }}
                showTooltip={false}
              >
                {merchant.salesRep}
              </TruncatedCell>
            </tr>
          );
        }

        // For full merchant tables, render all columns
        return (
          <tr 
            key={`merchant-${merchant.id || merchant.gwId || merchant.businessName}`}
            className="clickable-row"
            onClick={(e) => {
              e.preventDefault();
              handleBusinessClick(merchant);
            }}
            style={{ cursor: "pointer" }}
            role="button"
            tabIndex={0}
            title="" // Remove default tooltip - individual cells will handle their own
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBusinessClick(merchant);
              }
            }}
          >
            {/* Business Name */}
            <TruncatedCell 
              className="business-name-cell" 
              style={{ verticalAlign: 'middle' }}
              defaultTooltip="Click to view full details"
            >
              <span className="business-name-text">
                {merchant.businessName}
              </span>
            </TruncatedCell>

            {/* Conditional Sales Rep/Account Ex columns based on company */}
            {company === "CyberSource" ? (
              <TruncatedCell defaultTooltip="Click to view full details">
                {merchant.accountEx}
              </TruncatedCell>
            ) : (
              <TruncatedCell defaultTooltip="Click to view full details">
                {merchant.salesRep}
              </TruncatedCell>
            )}

            {/* Email */}
            <TruncatedCell defaultTooltip="Click to view full details">
              {merchant.email}
            </TruncatedCell>

            {/* GW ID */}
            <TruncatedCell defaultTooltip="Click to view full details">
              {merchant.gwId}
            </TruncatedCell>

            {/* Date columns */}
            <TruncatedCell defaultTooltip="Click to view full details">
              {merchant.dateSubmitted}
            </TruncatedCell>
            <TruncatedCell defaultTooltip="Click to view full details">
              {merchant.dateApproved}
            </TruncatedCell>
            <TruncatedCell defaultTooltip="Click to view full details">
              {merchant.dateUpdated}
            </TruncatedCell>

            {/* Status Badge - ellipsis for long text, full value in tooltip */}
            <TruncatedCell className="status-cell" defaultTooltip="Click to view full details" tooltipPlacement="left">
              <span className={`badge cBadge ${merchant.statusClass}`}>
                {merchant.status}
              </span>
            </TruncatedCell>
          </tr>
        );
      })}
    </tbody>
  );
});

TableBody.displayName = "TableBody";

export default TableBody;
